import { useEffect, useMemo, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Coins, ShoppingBag, Tag, History, X, Store, Sparkles, TrendingUp, Loader2, Package, Wand2, Swords, Target, RefreshCcw } from 'lucide-react';
import api from '../lib/api';
import { RARITY_UI } from '../lib/rarity';
import RarityBadge from '../components/RarityBadge';
import { toast } from 'sonner';

const TABS = [
  { id: 'browse',   label: 'Browse',      icon: Store },
  { id: 'mine',     label: 'My Listings', icon: Tag },
  { id: 'sell',     label: 'Sell',        icon: ShoppingBag },
  { id: 'merchant', label: 'NPC Merchant',icon: Sparkles },
  { id: 'bounties', label: 'Bounties',    icon: Target },
  { id: 'history',  label: 'History',     icon: History },
];

export default function Marketplace() {
  const [tab, setTab] = useState('browse');
  const [balance, setBalance] = useState(0);

  const refreshBalance = useCallback(() => {
    api.get('/coins/balance').then(r => setBalance(r.data.wildcoins)).catch(() => {});
  }, []);
  useEffect(() => { refreshBalance(); }, [refreshBalance]);

  return (
    <div className="space-y-5" data-testid="marketplace-root">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-accent">Marketplace</p>
          <h2 className="font-serif text-3xl">WildCoins Exchange</h2>
          <p className="text-xs text-muted-foreground mt-1">Trade cards, ingredients & potions with other explorers. XP is your legacy — WildCoins are your wallet.</p>
        </div>
        <div className="rounded-full bg-accent/10 border border-accent/30 px-3 py-1.5 flex items-center gap-1.5">
          <Coins className="w-3.5 h-3.5 text-accent" />
          <span data-testid="mkt-balance" className="font-mono text-sm tabular-nums text-accent">{balance}</span>
        </div>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="mkt-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`mkt-tab-${t.id}`}
              className={`flex-shrink-0 flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
                active ? 'border-accent/60 bg-accent/10 text-accent' : 'border-white/10 bg-card/50 text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="w-3 h-3" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'browse' && <BrowseTab balance={balance} onRefresh={refreshBalance} />}
      {tab === 'mine' && <MineTab onRefresh={refreshBalance} />}
      {tab === 'sell' && <SellTab onRefresh={refreshBalance} onListed={() => setTab('mine')} />}
      {tab === 'merchant' && <MerchantTab balance={balance} onRefresh={refreshBalance} />}
      {tab === 'bounties' && <BountiesTab onRefresh={refreshBalance} />}
      {tab === 'history' && <HistoryTab />}
    </div>
  );
}

// ---------- Browse ----------
function BrowseTab({ balance, onRefresh }) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterRarity, setFilterRarity] = useState('');
  const [sort, setSort] = useState('newest');

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (filterType) params.set('item_type', filterType);
    if (filterRarity) params.set('rarity', filterRarity);
    params.set('sort', sort);
    setLoading(true);
    api.get(`/marketplace/listings?${params.toString()}`)
      .then(r => setListings(r.data.listings || []))
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  }, [filterType, filterRarity, sort]);
  useEffect(() => { load(); }, [load]);

  const buy = async (l) => {
    try {
      await api.post(`/marketplace/${l.listing_id}/buy`);
      toast.success(`Bought ${l.snapshot.name} for ${l.price} WildCoins`);
      onRefresh();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purchase failed');
    }
  };

  return (
    <div className="space-y-3" data-testid="mkt-browse">
      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {[['', 'All'], ['card', 'Cards'], ['ingredient', 'Ingredients'], ['potion', 'Potions']].map(([k, l]) => (
          <button key={k} onClick={() => setFilterType(k)}
            data-testid={`mkt-filter-type-${k || 'all'}`}
            className={`flex-shrink-0 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${
              filterType === k ? 'border-accent/60 bg-accent/10 text-accent' : 'border-white/10 bg-card/50 text-muted-foreground'
            }`}>{l}</button>
        ))}
      </div>
      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {['', 'common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'].map(r => (
          <button key={r || 'any'} onClick={() => setFilterRarity(r)}
            data-testid={`mkt-filter-rarity-${r || 'any'}`}
            className={`flex-shrink-0 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${
              filterRarity === r ? 'border-accent/60 bg-accent/10 text-accent' : 'border-white/10 bg-card/50 text-muted-foreground'
            }`}>{r || 'Any'}</button>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <select value={sort} onChange={e => setSort(e.target.value)} data-testid="mkt-sort" className="bg-card border border-white/10 rounded-full text-[11px] px-3 py-1.5">
          <option value="newest">Newest first</option>
          <option value="price_asc">Price ↑</option>
          <option value="price_desc">Price ↓</option>
        </select>
        <button onClick={load} data-testid="mkt-refresh" className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground flex items-center gap-1">
          <RefreshCcw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-muted-foreground" /></div>
      ) : listings.length === 0 ? (
        <EmptyBox icon={<Store className="w-8 h-8 mx-auto text-muted-foreground" />} title="No listings" desc="Be the first to list an item!" />
      ) : (
        <div className="space-y-2">
          {listings.map(l => <ListingRow key={l.listing_id} listing={l} onBuy={() => buy(l)} disabled={balance < l.price} />)}
        </div>
      )}
    </div>
  );
}

function ListingRow({ listing, onBuy, disabled }) {
  const ui = RARITY_UI[listing.snapshot?.rarity] || RARITY_UI.common;
  return (
    <div className={`rounded-2xl p-3 border border-white/5 ${ui.cardClass}`} style={ui.cardStyle} data-testid="mkt-listing-row">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <RarityBadge rarity={listing.snapshot?.rarity || 'common'} size="sm" showParticles={false} />
            <span className="text-[9px] uppercase tracking-widest text-muted-foreground">{listing.item_type}</span>
            {listing.qty > 1 && <span className="text-[10px] font-mono text-muted-foreground">×{listing.qty}</span>}
          </div>
          <p className="font-serif text-sm mt-1 truncate">{listing.snapshot?.name}</p>
          <p className="text-[10px] text-muted-foreground truncate">
            by {listing.seller_name}
            {listing.item_type === 'card' && listing.snapshot?.meta?.battle_rating != null && (
              <> · Rating <span className="font-mono" style={{ color: ui.color }}>{listing.snapshot.meta.battle_rating}</span></>
            )}
            {listing.item_type === 'potion' && listing.snapshot?.meta?.effect_stat && (
              <> · +{listing.snapshot.meta.effect_value} {listing.snapshot.meta.effect_stat}</>
            )}
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="flex items-center gap-1 text-accent">
            <Coins className="w-3 h-3" />
            <span className="font-mono text-base tabular-nums" data-testid="mkt-listing-price">{listing.price}</span>
          </div>
          <button
            onClick={onBuy}
            disabled={disabled}
            data-testid="mkt-buy-btn"
            className="mt-1 text-[10px] uppercase tracking-widest px-3 py-1 rounded-full bg-accent text-black font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >Buy</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Mine ----------
function MineTab({ onRefresh }) {
  const [data, setData] = useState({ active: [], history: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/marketplace/mine').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    try {
      await api.post(`/marketplace/${id}/cancel`);
      toast.success('Listing cancelled — item returned');
      onRefresh(); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-muted-foreground" /></div>;

  return (
    <div className="space-y-4" data-testid="mkt-mine">
      <section>
        <h3 className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Active listings ({data.active.length})</h3>
        {data.active.length === 0 ? (
          <EmptyBox icon={<Tag className="w-6 h-6 mx-auto text-muted-foreground" />} title="No active listings" desc="Head to Sell tab to list something." />
        ) : (
          <div className="space-y-2">
            {data.active.map(l => (
              <div key={l.listing_id} className="rounded-2xl bg-card border border-white/5 p-3" data-testid="mkt-mine-active-row">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <RarityBadge rarity={l.snapshot?.rarity || 'common'} size="sm" showParticles={false} />
                      <span className="text-[9px] uppercase tracking-widest text-muted-foreground">{l.item_type}</span>
                    </div>
                    <p className="font-serif text-sm truncate">{l.snapshot?.name}</p>
                    <p className="text-[10px] text-muted-foreground">Listed for {new Date(l.created_at).toLocaleDateString()} · fee paid {l.listing_fee_paid}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-accent"><Coins className="w-3 h-3" /><span className="font-mono">{l.price}</span></div>
                    <button onClick={() => cancel(l.listing_id)} data-testid="mkt-cancel-btn" className="mt-1 text-[10px] uppercase tracking-widest text-red-300 hover:text-red-200">Cancel</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ---------- Sell ----------
function SellTab({ onRefresh, onListed }) {
  const [tab, setTab] = useState('ingredient');
  const [cards, setCards] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [potions, setPotions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [price, setPrice] = useState(20);
  const [qty, setQty] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/cards?limit=200').then(r => setCards((r.data || []).filter(c => !c.listed))).catch(() => {});
    api.get('/inventory').then(r => {
      setIngredients((r.data.ingredients || []).filter(i => (i.qty || 0) > 0));
      setPotions((r.data.potions || []).filter(p => !p.listed && !p.used));
    }).catch(() => {});
  }, []);

  const list = async () => {
    if (!selected) return toast.error('Select an item to list');
    if (price < 1) return toast.error('Price must be at least 1');
    setSubmitting(true);
    try {
      await api.post('/marketplace/list', {
        item_type: tab,
        item_ref: tab === 'card' ? selected.card_id : (tab === 'potion' ? selected.potion_id : selected.ingredient_id),
        price: Number(price),
        qty: tab === 'ingredient' ? Number(qty) : 1,
      });
      toast.success('Listed successfully');
      setSelected(null); setPrice(20); setQty(1);
      onRefresh();
      onListed?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Listing failed');
    } finally { setSubmitting(false); }
  };

  const items = tab === 'card' ? cards : (tab === 'potion' ? potions : ingredients);
  const fee = Math.max(2, Math.round(price * 0.05));
  const tax = Math.round(price * 0.10);
  const payout = Math.max(0, price - tax);

  return (
    <div className="space-y-3" data-testid="mkt-sell">
      <div className="flex items-center gap-2">
        {[['ingredient', 'Ingredient'], ['potion', 'Potion'], ['card', 'Card']].map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); setSelected(null); }}
            data-testid={`mkt-sell-tab-${k}`}
            className={`text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${
              tab === k ? 'border-accent/60 bg-accent/10 text-accent' : 'border-white/10 bg-card/50 text-muted-foreground'
            }`}>{l}</button>
        ))}
      </div>

      {items.length === 0 ? (
        <EmptyBox icon={<Package className="w-6 h-6 mx-auto text-muted-foreground" />} title={`No ${tab}s to sell`} desc="Get some by discovering, battling or crafting." />
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="mkt-sell-list">
          {items.map(it => {
            const key = tab === 'card' ? it.card_id : (tab === 'potion' ? it.potion_id : it.ingredient_id);
            const name = tab === 'card' ? it.species_common_name : (tab === 'potion' ? it.name : (it.display_name || it.name));
            const rarity = it.rarity || 'common';
            const active = selected && key === (tab === 'card' ? selected.card_id : (tab === 'potion' ? selected.potion_id : selected.ingredient_id));
            const ui = RARITY_UI[rarity] || RARITY_UI.common;
            return (
              <button key={key} type="button" onClick={() => setSelected(it)}
                data-testid="mkt-sell-item"
                className={`w-full text-left rounded-2xl p-3 border ${active ? 'ring-2 ring-accent border-accent' : 'border-white/10'} ${ui.cardClass}`}
                style={ui.cardStyle}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <RarityBadge rarity={rarity} size="sm" showParticles={false} />
                      <span className="font-serif text-sm truncate">{name}</span>
                    </div>
                    {tab === 'ingredient' && <p className="text-[10px] text-muted-foreground">Have {it.qty}</p>}
                    {tab === 'card' && <p className="text-[10px] text-muted-foreground">Rating {it.battle_rating}</p>}
                    {tab === 'potion' && <p className="text-[10px] text-muted-foreground">+{it.effect_value} {it.effect_stat}</p>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selected && (
        <div className="rounded-2xl bg-card border border-white/10 p-3 space-y-3" data-testid="mkt-sell-form">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">List details</p>
          <label className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Price (WildCoins)</span>
            <input type="number" min={1} value={price} onChange={e => setPrice(Number(e.target.value))}
              data-testid="mkt-sell-price"
              className="w-24 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-right font-mono" />
          </label>
          {tab === 'ingredient' && (
            <label className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Quantity</span>
              <input type="number" min={1} max={selected.qty} value={qty} onChange={e => setQty(Number(e.target.value))}
                data-testid="mkt-sell-qty"
                className="w-24 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-right font-mono" />
            </label>
          )}
          <div className="grid grid-cols-3 gap-2 text-center">
            <FeeCell label="Fee (5%)" value={`-${fee}`} accent="#f87171" />
            <FeeCell label="Tax on sale (10%)" value={`-${tax}`} accent="#f87171" />
            <FeeCell label="You receive" value={`+${payout}`} accent="#4ade80" />
          </div>
          <button onClick={list} disabled={submitting} data-testid="mkt-sell-submit"
            className="w-full py-3 rounded-full bg-accent text-black font-semibold disabled:opacity-40">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : `List for ${price} WildCoins`}
          </button>
        </div>
      )}
    </div>
  );
}

function FeeCell({ label, value, accent }) {
  return (
    <div className="rounded-lg bg-black/30 border border-white/5 py-2">
      <p className="font-mono text-sm tabular-nums" style={{ color: accent }}>{value}</p>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

// ---------- NPC Merchant ----------
function MerchantTab({ balance, onRefresh }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState({ ingredients: [] });

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/merchant/inventory').then(r => r.data.inventory || []),
      api.get('/inventory').then(r => r.data).catch(() => ({ ingredients: [] })),
    ]).then(([inv, myinv]) => {
      setItems(inv);
      setInventory(myinv);
    }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const buy = async (item) => {
    try {
      await api.post('/merchant/buy', { sku: item.sku, qty: 1 });
      toast.success(`Bought 1× ${item.name} for ${item.price} WildCoins`);
      onRefresh(); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purchase failed');
    }
  };

  const sell = async (ing) => {
    try {
      const { data } = await api.post('/merchant/sell', { item_type: 'ingredient', item_id: ing.ingredient_id, qty: 1 });
      toast.success(`Sold 1× ${ing.display_name || ing.name} for ${data.price_each} WildCoins`);
      onRefresh(); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sell failed');
    }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-muted-foreground" /></div>;

  return (
    <div className="space-y-4" data-testid="mkt-merchant">
      <div className="rounded-2xl bg-gradient-to-br from-emerald-900/40 to-emerald-700/10 border border-emerald-400/20 p-3">
        <p className="text-[10px] uppercase tracking-widest text-emerald-300">Today's wares</p>
        <p className="text-xs text-muted-foreground mt-1">Merchant rotates daily. Prices drift ±15% each day.</p>
      </div>
      <div className="space-y-2">
        {items.map(it => (
          <div key={it.sku} className="rounded-2xl bg-card border border-white/5 p-3" data-testid="mkt-merchant-item">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <RarityBadge rarity={it.rarity} size="sm" showParticles={false} />
                  <span className="font-serif text-sm truncate">{it.name}</span>
                </div>
                <p className="text-[10px] text-muted-foreground">{it.desc} · stock {it.remaining}/{it.stock}</p>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1 text-accent"><Coins className="w-3 h-3" /><span className="font-mono">{it.price}</span></div>
                <button onClick={() => buy(it)} disabled={balance < it.price || it.remaining <= 0}
                  data-testid="mkt-merchant-buy"
                  className="mt-1 text-[10px] uppercase tracking-widest px-3 py-1 rounded-full bg-accent text-black font-semibold disabled:opacity-40">
                  {it.remaining <= 0 ? 'Sold out' : 'Buy'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div>
        <h3 className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Sell to merchant (40% of buy price)</h3>
        {inventory.ingredients?.length ? (
          <div className="space-y-2">
            {inventory.ingredients.slice(0, 20).map(ing => (
              <div key={ing.ingredient_id} className="rounded-2xl bg-card border border-white/5 p-3 flex items-center justify-between" data-testid="mkt-merchant-sell-row">
                <div className="flex items-center gap-2 min-w-0">
                  <RarityBadge rarity={ing.rarity || 'common'} size="sm" showParticles={false} />
                  <span className="font-serif text-sm truncate">{ing.display_name || ing.name}</span>
                  <span className="text-[10px] font-mono text-muted-foreground">×{ing.qty}</span>
                </div>
                <button onClick={() => sell(ing)} data-testid="mkt-merchant-sell"
                  className="text-[10px] uppercase tracking-widest px-3 py-1 rounded-full bg-white/10 hover:bg-white/15 text-foreground">
                  Sell 1
                </button>
              </div>
            ))}
          </div>
        ) : <EmptyBox icon={<Package className="w-6 h-6 mx-auto text-muted-foreground" />} title="No ingredients" desc="Discover plants/mushrooms/trees to gather materials." />}
      </div>
    </div>
  );
}

// ---------- Bounties ----------
function BountiesTab({ onRefresh }) {
  const [data, setData] = useState({ bounties: [], reroll_cost: 15 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/bounties/daily').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const claim = async (b) => {
    try {
      const { data } = await api.post(`/bounties/${b.bounty_id}/claim`);
      toast.success(`Claimed +${data.reward} WildCoins`);
      onRefresh(); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Claim failed');
    }
  };

  const reroll = async () => {
    if (!window.confirm(`Reroll bounties for ${data.reroll_cost} WildCoins?`)) return;
    try {
      await api.post('/bounties/reroll');
      toast.success('Bounties rerolled');
      onRefresh(); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reroll failed');
    }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-muted-foreground" /></div>;

  return (
    <div className="space-y-3" data-testid="mkt-bounties">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">Daily bounties reset at UTC midnight.</p>
        <button onClick={reroll} data-testid="mkt-bounty-reroll" className="text-[10px] uppercase tracking-widest text-accent flex items-center gap-1">
          <RefreshCcw className="w-3 h-3" /> Reroll ({data.reroll_cost} coins)
        </button>
      </div>
      {data.bounties.map(b => {
        const pct = Math.min(100, Math.round(((b.progress || 0) / b.count) * 100));
        return (
          <div key={b.bounty_id} className="rounded-2xl bg-card border border-white/5 p-3" data-testid="mkt-bounty-row">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-serif text-sm">{b.label}</p>
                <p className="text-[10px] text-muted-foreground">{b.desc}</p>
              </div>
              <div className="flex items-center gap-1 text-accent shrink-0">
                <Coins className="w-3 h-3" /><span className="font-mono text-sm">{b.reward}</span>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full bg-emerald-400/70" style={{ width: `${pct}%` }} />
              </div>
              <span className="font-mono text-[10px] tabular-nums text-muted-foreground">{b.progress || 0}/{b.count}</span>
              {b.claimed ? (
                <span className="text-[10px] uppercase tracking-widest text-emerald-400" data-testid="mkt-bounty-claimed">Claimed</span>
              ) : b.completed ? (
                <button onClick={() => claim(b)} data-testid="mkt-bounty-claim" className="text-[10px] uppercase tracking-widest px-3 py-1 rounded-full bg-accent text-black font-semibold">Claim</button>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------- History ----------
function HistoryTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/marketplace/history').then(r => setRows(r.data.history || [])).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="p-8 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-muted-foreground" /></div>;
  if (rows.length === 0) return <EmptyBox icon={<History className="w-6 h-6 mx-auto text-muted-foreground" />} title="No trades yet" desc="Your buy/sell history will show here." />;
  return (
    <div className="space-y-2" data-testid="mkt-history">
      {rows.map(h => (
        <div key={h.listing_id + h.sold_at} className="rounded-2xl bg-card border border-white/5 p-3 flex items-center justify-between" data-testid="mkt-history-row">
          <div className="min-w-0 flex-1">
            <p className="font-serif text-sm truncate">{h.snapshot?.name}</p>
            <p className="text-[10px] text-muted-foreground">{new Date(h.sold_at).toLocaleString()}</p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 text-accent"><Coins className="w-3 h-3" /><span className="font-mono">{h.price}</span></div>
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground">tax {h.tax}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyBox({ icon, title, desc }) {
  return (
    <div className="rounded-3xl bg-card border border-white/5 p-8 text-center">
      {icon}
      <p className="font-serif text-lg mt-2">{title}</p>
      <p className="text-xs text-muted-foreground mt-1">{desc}</p>
    </div>
  );
}
